import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  GlobalStyles,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { NewFormContext } from '../../context/NewFormContext';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import Clients from '../../service/Clients';
import { googleMapsService } from '../../service/Google';
import { ChecklistType, hierarchyResponse } from '../../types/checklist/types';
import { chamadosAssociacoesType } from '../../types/reports/types';

const clienteService = new Clients();

export default function PreenchimentoWeb() {
  const { modelo, setModelo } = useContext(NewFormContext);
  const { hierarchyLabel } = useContext(UserContext);
  const [expand, setExpand] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState({
    cliente: '',
    local: '',
    conjunto: '',
    unidade: ''
  });
  const { t } = useTranslation('translation');

  const [chamadoAssociacoes, setChamadoAssociacoes] = useState<chamadosAssociacoesType | null>(null);
  const linkRef = useRef<HTMLInputElement>(null);
  const publicLinkRef = useRef<HTMLInputElement>(null);
  const enderecoCercaRef = useRef<HTMLInputElement>(null);
  const [enderecoCerca, setEnderecoCerca] = useState('');
  const cercaAtiva = Boolean(modelo?.cerca);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['HIERARQUIA'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const result: hierarchyResponse = await clienteService.getAllHierarchy(token);
          if (!result) throw new Error('No hierarchy data found');
          return result;
        }
      } catch (error) {
        console.error('Error fetching hierarchy data:', error);
        throw error;
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const handleTicketAfter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setModelo((prev: ChecklistType) => prev ? {
      ...prev, abreChamado: {
        apos: event.target.checked,
        durante: prev.abreChamado?.apos ? !prev.abreChamado?.durante : true,
      }
    } : prev
    );
  };

  const getValuesForLink = () => {
    const newLink = (Object.values(chamadoAssociacoes as chamadosAssociacoesType) || [])?.filter(link => !['cliente', 'local', 'conjunto', 'unidade'].includes(link?.nome as string));
    const updatedLinks = newLink.map((item, index) => ({
      index: index,
      name: ['cliente', 'local', 'conjunto', 'unidade'][index] || item?.nome,
      value: item?.id || 'all',
      blocked: true,
    }));
    setModelo((prev: ChecklistType) => {
      if (!prev) return prev;

      return {
        ...prev,
        link: updatedLinks,
      };
    });
  };

  function handelAddItemOnLink() {
    setModelo((prev: ChecklistType) => prev ? { ...prev, link: [...prev.link, { name: '', value: '', index: modelo?.link.length }] } : prev);
  }

  function handleRemoveItemOnLink(index: number) {
    setModelo((prev: ChecklistType) => prev ? { ...prev, link: prev.link.filter((_, i) => i !== index) } : prev);
  }

  function handleLinkNameChange(index: number, name: string) {
    if (['conjunto', 'local', 'cliente', 'unidade']?.includes(name)) {
      toast.warn(`${t('toast.forbiddenWord')}`);
    } else setModelo((prev: ChecklistType) => {
      if (!prev) return prev;
      const newLink = prev.link?.map((item, i) => i === index ? { ...item, name } : item);
      return { ...prev, link: newLink };
    });
  };

  function handleLinkValueChange(index: number, value: string) {
    setModelo((prev: ChecklistType) => {
      if (!prev) return prev;
      const newLink = prev.link.map((item, i) => i === index ? { ...item, value } : item);
      return { ...prev, link: newLink };
    });
  }

  function handleRemoveAllLinkValues() {
    setChamadoAssociacoes(null);
    setModelo((prev: ChecklistType) => prev ? { ...prev, link: null } : prev);
    setInputValue({
      cliente: '',
      local: '',
      conjunto: '',
      unidade: ''
    });
  }

  function updateUrlPublica(campo: 'aliasEmpresa' | 'aliasChecklist', valor: string) {
    setModelo((prev: ChecklistType) => prev ? {
      ...prev,
      urlPublica: { ...prev.urlPublica, [campo]: valor },
    } : prev);
  }

  function atualizarCerca(campo: 'raioMetros', valor: number) {
    setModelo((prev: ChecklistType) => prev?.cerca ? {
      ...prev,
      cerca: { ...prev.cerca, [campo]: valor },
    } : prev);
  }

  function alterarCercaAtiva(ativa: boolean) {
    if (!ativa) setEnderecoCerca('');
    setModelo((prev: ChecklistType) => prev ? {
      ...prev,
      cerca: ativa ? prev.cerca ?? { latitude: 91, longitude: 181, raioMetros: 100 } : undefined,
    } : prev);
  }

  useEffect(() => {
    if (!enderecoCercaRef.current) return;
    void googleMapsService.createAutocomplete(enderecoCercaRef.current, (place) => {
      const latitude = place.geometry?.location?.lat();
      const longitude = place.geometry?.location?.lng();
      const endereco = place.formatted_address ?? '';
      if (latitude === undefined || longitude === undefined || !endereco) return;
      setModelo((prev: ChecklistType) => prev?.cerca ? {
        ...prev,
        cerca: { ...prev.cerca, latitude, longitude },
      } : prev);
      setEnderecoCerca(endereco);
    });
  }, [cercaAtiva, setModelo]);

  useEffect(() => {
    const cerca = modelo?.cerca;
    if (!cercaAtiva || enderecoCerca || !cerca ||
      cerca.latitude < -90 || cerca.latitude > 90 ||
      cerca.longitude < -180 || cerca.longitude > 180) return;

    void googleMapsService.getAddressByCoordinates(cerca.latitude, cerca.longitude)
      .then((endereco) => setEnderecoCerca(endereco ?? ''))
      .catch(() => setEnderecoCerca(''));
  }, [cercaAtiva, enderecoCerca, modelo?.cerca]);

  const link = modelo?.link?.map((e, i) => {
    if (e) {
      if (i === 0) return `${process.env.REACT_APP_AXYMA_FRONT_URL}preenchedor/${modelo.id}?${e.name}=${e.value}`;
      else return `&${e.name}=${e.value}`;
    } else return '';
  })?.join('')
    || `${process.env.REACT_APP_AXYMA_FRONT_URL}preenchedor/${modelo?.id}`;

  const urlPublica = modelo?.urlPublica;
  const aliasesConfigurados = Boolean(urlPublica?.aliasEmpresa?.trim() && urlPublica?.aliasChecklist?.trim());
  const baseUrlPublica = (process.env.REACT_APP_AXYMA_FRONT_URL ?? window.location.origin).replace(/\/?$/, '/');
  const linkPublico = aliasesConfigurados
    ? `${baseUrlPublica}${encodeURIComponent(urlPublica?.aliasEmpresa?.trim() ?? '')}/${encodeURIComponent(urlPublica?.aliasChecklist?.trim() ?? '')}`
    : '';
  return (
    <Card sx={{ width: '100%', padding: 2 }}>
      <Stack
        width='100%'
        direction="column"
        justifyContent="center"
        alignItems='flex-start'
        spacing={2}
      >
        <Typography color='textDisabled' variant='h5' textAlign='center' fontWeight={600} width='100%'>{t('checklists.publicAccess.webFillingSettings')}</Typography>
        <Stack
          width={'100%'}
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
        >
          <TextField
            multiline
            inputRef={linkRef}
            variant='outlined'
            fullWidth
            disabled
            label={t('checklists.subitemModal.preenchimento.Link')}
            value={link}
          />
          <IconButton color='success'>
            <Tooltip
              onClick={async () => {
                await navigator.clipboard.writeText(linkRef.current?.value as string);
                alert(`${t('checklists.formModal.linkCopiedToClipboard')}`);
              }}
              title={` ${t('checklists.subitemModal.preenchimento.copiar')}`}>
              <ContentCopyIcon fontSize='large' />
            </Tooltip>
          </IconButton>
        </Stack>
        {!modelo?.preenchimentoDeslogado && (
          <Typography color='textSecondary' variant='body2'>
            {t('checklists.publicAccess.anonymousOnly')}
          </Typography>
        )}
        {modelo?.preenchimentoDeslogado && <Card variant='outlined' sx={{ width: '100%', padding: 2 }}>
          <Stack spacing={2}>
            <Stack spacing={.5}>
              <Typography color='textDisabled' variant='h6' fontWeight={600}>
                {t('checklists.publicAccess.title')}
              </Typography>
              <Typography color='textSecondary' variant='body2'>
                {t('checklists.publicAccess.anonymousOnly')}
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t('checklists.publicAccess.companyAlias')}
                value={urlPublica?.aliasEmpresa ?? ''}
                onChange={(event) => updateUrlPublica('aliasEmpresa', event.target.value)}
              />
              <TextField
                fullWidth
                label={t('checklists.publicAccess.checklistAlias')}
                value={urlPublica?.aliasChecklist ?? ''}
                onChange={(event) => updateUrlPublica('aliasChecklist', event.target.value)}
              />
            </Stack>
            <Card variant='outlined' sx={{ padding: 2 }}>
              <Stack spacing={2}>
                <GlobalStyles styles={{ '.pac-container': { zIndex: 2000 } }} />
                <FormControlLabel
                  control={<Checkbox checked={cercaAtiva} onChange={(event) => alterarCercaAtiva(event.target.checked)} />}
                  label={t('checklists.publicAccess.geofenceEnabled')}
                />
                <Typography color='textSecondary' variant='body2'>
                  {t('checklists.publicAccess.geofenceHelp')}
                </Typography>
                {modelo?.cerca && <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    required
                    inputRef={enderecoCercaRef}
                    label={t('address')}
                    value={enderecoCerca}
                    helperText={t('checklists.publicAccess.addressHelp')}
                    onChange={(event) => setEnderecoCerca(event.target.value)}
                  />
                  <TextField
                    required
                    type='number'
                    sx={{ width: { xs: '100%', md: 190 }, flexShrink: 0 }}
                    label={t('checklists.publicAccess.radiusMeters')}
                    value={modelo.cerca.raioMetros}
                    inputProps={{ min: 1, step: 1 }}
                    onChange={(event) => atualizarCerca('raioMetros', Number(event.target.value))}
                  />
                </Stack>}
              </Stack>
            </Card>
            {aliasesConfigurados ? (
              <Stack direction='row' spacing={1} alignItems='center'>
                <TextField
                  fullWidth
                  inputRef={publicLinkRef}
                  label={t('checklists.publicAccess.preview')}
                  value={linkPublico}
                  disabled
                />
                <Tooltip title={t('checklists.subitemModal.preenchimento.copiar')}>
                  <IconButton
                    color='success'
                    onClick={async () => {
                      await navigator.clipboard.writeText(publicLinkRef.current?.value ?? linkPublico);
                      toast.success(t('checklists.formModal.linkCopiedToClipboard'));
                    }}
                  >
                    <ContentCopyIcon fontSize='large' />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Typography color='textSecondary' variant='body2'>
                {t('checklists.publicAccess.legacyUrl')}
              </Typography>
            )}
          </Stack>
        </Card>}
        {modelo?.link?.map((e, i) => (
          <Stack
            key={i}
            width={'100%'}
            direction="row"
            justifyContent='flex-start'
            alignItems="center"
            spacing={1}
          >
            <TextField
              label={t('name')}
              variant='outlined'
              disabled={e.blocked}
              value={modelo.link[i].name}
              onChange={(e) => handleLinkNameChange(i, e.target.value)}
            />
            <TextField
              fullWidth
              variant='outlined'
              label={t('checklists.subitemModal.preenchimento.valor')}
              disabled={modelo?.link[i].name === '' || e.blocked}
              value={modelo.link[i].value}
              onChange={(e) => handleLinkValueChange(i, e.target.value)}
            />
            {i === 0 ?
              <IconButton onClick={handelAddItemOnLink} color='success'>
                <AddCircleIcon fontSize='large' />
              </IconButton>
              :
              <IconButton onClick={() => handleRemoveItemOnLink(i)} color='error'>
                <RemoveCircleIcon fontSize='large' />
              </IconButton>
            }
          </Stack>
        ))}
        <FormControlLabel
          control={<Checkbox
            checked={modelo?.abreChamado?.apos || false}
            onChange={handleTicketAfter}
          />} label={t('checklists.allowActionPlanOpening')} />
        {!chamadoAssociacoes?.cliente &&
          <Typography color='textDisabled' fontWeight={600} textAlign='center' width='100%'>
            {t('checklists.formModal.pleaseAssociateHierarchyForCallOpening')}
          </Typography>}
        {(
          <Card sx={{ width: '100%', padding: 2 }}>
            <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center' padding={1}>
              <Typography color='textDisabled' variant='h6' fontWeight={600} textAlign='center' width='100%'>
                {t('checklists.formModal.hierarchyConfigurationForCallOpening')}
              </Typography>
              <IconButton color='info' onClick={() => setExpand(!expand)}>
                {expand ? <VisibilityOffIcon />
                  : <RemoveRedEyeIcon />}
              </IconButton>
            </Stack>
            {expand &&
              <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                spacing={2}
              >
                {chamadoAssociacoes?.cliente && (
                  <Typography variant='body2' fontWeight='bold' sx={{ backgroundColor: '#FEE08A', padding: 1.5, borderRadius: '5px' }}>
                    {
                      chamadoAssociacoes.cliente && !chamadoAssociacoes.local ? (
                        `${t('checklists.formModal.teamsServingAll')} ${hierarchyLabel.conjunto} ${t('checklists.formModal.ofAll')} ${hierarchyLabel.local} ${t('checklists.formModal.of')} ${hierarchyLabel.cliente} ${chamadoAssociacoes.cliente.nome} ${t('checklists.formModal.willReceiveCalls')}`
                      ) : chamadoAssociacoes.local && !chamadoAssociacoes.conjunto ? (
                        `${t('checklists.formModal.teamsServingAll')} ${hierarchyLabel.conjunto} ${t('checklists.formModal.of')} ${hierarchyLabel.local} ${chamadoAssociacoes.local.nome} ${t('checklists.formModal.willReceiveCalls')}`
                      ) : chamadoAssociacoes.conjunto ? (
                        `${t('checklists.formModal.teamsServingSpecific')} ${hierarchyLabel.conjunto} ${chamadoAssociacoes.conjunto.nome} ${t('checklists.formModal.willReceiveCalls')}`
                      ) : ('')
                    }
                  </Typography>
                )}
                <Autocomplete
                  disabled={isLoading || isFetching}
                  disableClearable
                  inputValue={inputValue.cliente ?? ''}
                  fullWidth
                  noOptionsText={`Não existem clientes nesta empresa...`}
                  getOptionLabel={option => option.nome}
                  getOptionKey={option => option?.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel.cliente} />}
                  options={
                    data?.clientes
                      ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                      ?.map((cliente) => ({
                        ...cliente
                      })) || []}
                  onChange={(_event, newValue) => {
                    setChamadoAssociacoes({ cliente: newValue, local: null, conjunto: null, unidade: null });
                    setInputValue({ cliente: newValue.nome, local: '', conjunto: '', unidade: '' });
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      <span>{option?.nome}</span>
                    </li>)}
                />
                <Autocomplete
                  disableClearable
                  inputValue={inputValue.local ?? ''}
                  disabled={!chamadoAssociacoes?.cliente}
                  getOptionLabel={option => option.nome}
                  getOptionKey={option => option?.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={'Sem locais associados...'}
                  fullWidth
                  renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel?.local} />}
                  options={data?.locais.filter((cl) => cl.cliente === chamadoAssociacoes?.cliente?.id)
                    ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                    ?.map((local) => ({
                      ...local
                    })) || []}
                  onChange={(_event, newValue) => {
                    setChamadoAssociacoes(prev => prev && { ...prev, local: newValue, conjunto: null, unidade: null });
                    setInputValue(prev => ({ ...prev, local: newValue.nome, conjunto: '', unidade: '' }));
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      <span>{option?.nome}</span>
                    </li>)}
                />
                <Autocomplete
                  disableClearable
                  inputValue={inputValue.conjunto ?? ''}
                  disabled={!chamadoAssociacoes?.local}
                  noOptionsText={'Sem conjuntos associados...'}
                  fullWidth
                  getOptionLabel={option => option.nome}
                  getOptionKey={option => option?.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel?.conjunto} />}
                  options={data?.conjuntos?.filter((cnj) => cnj.local === chamadoAssociacoes?.local?.id)
                    ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                    ?.map((conjunto) => ({
                      ...conjunto
                    })) || []}
                  onChange={(_event, newValue) => {
                    setChamadoAssociacoes(prev => prev && { ...prev, conjunto: newValue, unidade: null });
                    setInputValue(prev => ({ ...prev, conjunto: newValue.nome, unidade: '' }));
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      <span>{option?.nome}</span>
                    </li>)}
                />
                <Autocomplete
                  disableClearable
                  inputValue={inputValue.unidade ?? ''}
                  disabled={!chamadoAssociacoes?.conjunto}
                  noOptionsText={'Sem unidades associadas...'}
                  fullWidth
                  getOptionLabel={option => option.nome}
                  getOptionKey={option => option?.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} variant='outlined' label={hierarchyLabel?.unidade} />}
                  options={data?.unidades?.filter((uni) => uni.conjunto?.includes(chamadoAssociacoes?.conjunto?.id as string))
                    ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                    ?.map((unidade) => ({
                      ...unidade
                    })) || []}
                  onChange={(_event, newValue) => {
                    setChamadoAssociacoes((prev) => prev && { ...prev, unidade: newValue as unknown as chamadosAssociacoesType['unidade'] });
                    setInputValue(prev => ({ ...prev, unidade: newValue.nome }));
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option?.id}>
                      <span>{option?.nome}</span>
                    </li>)}
                />
                <Stack
                  sx={{ width: '100%' }}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button
                    variant='contained'
                    color={(!chamadoAssociacoes || !chamadoAssociacoes.cliente) ? 'warning' : 'success'}
                    endIcon={<TaskAltIcon />}
                    disabled={(!chamadoAssociacoes || !chamadoAssociacoes.cliente)}
                    onClick={() => getValuesForLink()}
                  >
                    {(!chamadoAssociacoes || !chamadoAssociacoes.cliente)
                      ? `${t('checklists.formModal.selectAtLeastOne')} ${hierarchyLabel.cliente} ${t('checklists.formModal.toAssociate')}`
                      : `${t('checklists.formModal.applyValuesToLink')}`}
                  </Button>
                  <Button
                    endIcon={<DeleteIcon />}
                    variant='contained'
                    color='error'
                    onClick={handleRemoveAllLinkValues}
                  >
                    {t('checklists.formModal.clearLinkValues')}
                  </Button>
                </Stack>
              </Stack>}
          </Card>)}
      </Stack>
    </Card>
  );
}
